import type { PreparedAsset, ResultContent, Selection } from './types';
import { categoryLabels, playbooks } from './playbooks';
import { overviewLabels } from './selector';

export const assetToText = (asset: PreparedAsset): string =>
  [
    asset.title.toUpperCase(),
    '',
    ...asset.blocks.map((b) => `${b.heading}\n${b.body}`),
    asset.prerequisiteChecks.length ? `\nBefore you use this:\n${asset.prerequisiteChecks.map((c) => `- ${c}`).join('\n')}` : '',
    asset.readiness === 'NEEDS_RECIPIENT_OR_DESTINATION_CHECK'
      ? '\nNote: this draft contains details you must check or create yourself. Nothing here has been verified for you.'
      : '',
  ]
    .filter(Boolean)
    .join('\n\n')
    .trim();

export const fullPlanToText = (result: ResultContent, selection: Selection): string => {
  const secondaries = selection.secondaries.map((id) => playbooks[id]);
  const lines = [
    'YOUR FANBASE FIX',
    result.headline,
    '',
    `START HERE: ${result.startHereLabel}`,
    result.startHereBody,
    result.provisionalNote ? `\n${result.provisionalNote}` : '',
    '',
    'WHY THIS IS YOUR FIRST PRIORITY',
    ...result.basis.map((b) => `- ${b.paraphrase} (${b.answerRef}, ${b.basisType.toLowerCase().replace(/_/g, ' ')})`),
    '',
    'YOUR NEXT MOVE, PREPARED',
    assetToText(result.asset),
    '',
    'TRY THIS FIRST',
    `${result.firstAction.instruction} (about ${result.firstAction.approximateEffortMinutes} minutes)`,
    '',
    'WHAT TO LOOK FOR',
    result.measurement.mainObservation,
    `Window: ${result.measurement.window}`,
    `Limitation: ${result.measurement.limitation}`,
    '',
    'THEN WHAT?',
    `Repeat when: ${result.decisionRule.repeatWhen}`,
    `Adjust when: ${result.decisionRule.adjustWhen}`,
    `Not enough information when: ${result.decisionRule.insufficientInformationWhen}`,
    '',
    'WHAT CAN WAIT',
    `${result.whatCanWait.activity} ${result.whatCanWait.reason}`,
  ];

  if (secondaries.length) {
    lines.push('', 'NEXT TO CHECK');
    secondaries.forEach((s) => lines.push(`${categoryLabels[s.category]}: ${s.name}.`));
  }

  lines.push('', 'YOUR SIX-AREA OVERVIEW');
  (Object.keys(selection.overview) as (keyof typeof selection.overview)[]).forEach((cat) => {
    const o = selection.overview[cat];
    lines.push(`${categoryLabels[cat]}: ${overviewLabels[o.state]}. ${o.note}`);
  });

  lines.push(
    '',
    'Prepared from the information you supplied using DK\'s framework. Your audio and private accounts have not been reviewed.',
  );

  return lines.filter((l) => l !== undefined).join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
